import { Formik } from 'formik';
import { ProjectStatus } from 'generated/graphql';
import { isAfterDate, isBeforeDate, isDateEqual } from 'helpers/date';
import * as yup from 'yup';
import { Modal, TMultiselectOption } from '@spotted-zebra-uk/ui-components';
import styles from './ProjectFilterModal.module.scss';
import {
  AtsLinkedFilter,
  emptyStageTypeFilterValues,
  StageTypeFilterValues,
  stageTypeFiltersValidationSchema,
} from './ProjectsFilterModal.constants';
import ProjectsFilterModalForm from './ProjectsFilterModalForm';

export type FiltersFormValues = {
  isArchived: boolean;
  companies: TMultiselectOption[];
  hiringTeamUsers: TMultiselectOption[];
  atsLinked: AtsLinkedFilter;
  stageTypeFilters: StageTypeFilterValues;
  dateCreatedFrom: string;
  dateCreatedTo: string;
  datePublishedFrom: string;
  datePublishedTo: string;
  dateLastEditedFrom: string;
  dateLastEditedTo: string;
  projectCandidatesFrom: string;
  projectCandidatesTo: string;
  status: TMultiselectOption<ProjectStatus>[];
};

type FiltersModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValues: FiltersFormValues;
  onSubmit: (values: FiltersFormValues) => void;
};

export const emptyProjectFilterFormValues: FiltersFormValues = {
  isArchived: false,
  companies: [] as TMultiselectOption[],
  hiringTeamUsers: [] as TMultiselectOption[],
  atsLinked: AtsLinkedFilter.Any,
  stageTypeFilters: emptyStageTypeFilterValues(),
  dateCreatedFrom: '',
  dateCreatedTo: '',
  datePublishedFrom: '',
  datePublishedTo: '',
  dateLastEditedFrom: '',
  dateLastEditedTo: '',
  projectCandidatesFrom: '',
  projectCandidatesTo: '',
  status: [] as TMultiselectOption<ProjectStatus>[],
};

const isCandidatesBoundSet = (
  value?: number | string | null
): value is number => value !== undefined && value !== null && value !== '';

const toDateValidation = (value?: string, fromValue?: string): boolean => {
  return fromValue && value
    ? isAfterDate(value, fromValue) || isDateEqual(value, fromValue)
    : true;
};

const fromDateValidation = (value?: string, toValue?: string): boolean => {
  return toValue && value
    ? isBeforeDate(value, toValue) || isDateEqual(value, toValue)
    : true;
};

const validationSchema = yup.object().shape({
  isArchived: yup.boolean(),
  companies: yup.array().of(
    yup.object().shape({
      label: yup.string(),
      value: yup.string(),
    })
  ),
  status: yup.array().of(
    yup.object().shape({
      label: yup.string(),
      value: yup.string(),
    })
  ),
  projectCandidatesFrom: yup
    .number()
    .typeError('Must be a whole number')
    .integer('Must be a whole number')
    .min(0, "Can't be negative")
    .test(
      'projectCandidateFrom',
      "Must not be greater than the 'To' value",
      // A range of 0 to 0 is meaningful (roles with no candidates), so both
      // bounds are compared against undefined rather than for truthiness, and
      // equal values are allowed.
      (value, { parent }) =>
        isCandidatesBoundSet(value) &&
        isCandidatesBoundSet(parent.projectCandidatesTo)
          ? value <= Number(parent.projectCandidatesTo)
          : true
    ),
  projectCandidatesTo: yup
    .number()
    .typeError('Must be a whole number')
    .integer('Must be a whole number')
    .min(0, "Can't be negative")
    .test(
      'projectCandidateTo',
      "Must not be smaller than the 'From' value",
      (value, { parent }) =>
        isCandidatesBoundSet(value) &&
        isCandidatesBoundSet(parent.projectCandidatesFrom)
          ? value >= Number(parent.projectCandidatesFrom)
          : true
    ),
  stageTypeFilters: stageTypeFiltersValidationSchema,
  dateCreatedFrom: yup
    .string()
    .test(
      'dateCreatedFrom',
      'Must be earlier than "To" date',
      (value, { parent }) => fromDateValidation(value, parent.dateCreatedTo)
    ),
  dateCreatedTo: yup
    .string()
    .test(
      'dateCreatedTo',
      'Must be later than "From" date',
      (value, { parent }) => toDateValidation(value, parent.dateCreatedFrom)
    ),
  datePublishedFrom: yup
    .string()
    .test(
      'datePublishedFrom',
      'Must be earlier than "To" date',

      (value, { parent }) => fromDateValidation(value, parent.datePublishedTo)
    ),
  datePublishedTo: yup
    .string()
    .test(
      'datePublishedTo',
      'Must be later than "From" date',
      (value, { parent }) => toDateValidation(value, parent.datePublishedFrom)
    ),
  dateLastEditedFrom: yup
    .string()
    .test(
      'dateLastEditedFrom',
      'Must be earlier than "To" date',
      (value, { parent }) => fromDateValidation(value, parent.dateLastEditedTo)
    ),
  dateLastEditedTo: yup
    .string()
    .test(
      'dateLastEditedTo',
      'Must be later than "From" date',
      (value, { parent }) => toDateValidation(value, parent.dateLastEditedFrom)
    ),
});

const ProjectsFilterModal = ({
  isOpen,
  onClose,
  initialValues,
  onSubmit,
}: FiltersModalProps) => {
  const handleSubmit = (formValues: FiltersFormValues) => {
    onSubmit(formValues);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Filter"
      className={styles.projectsFilterModal}
      data-testid="projects-page-filter-modal"
    >
      <Formik
        // Remounts the form when the modal reopens, so the tab returns to Role
        // and any abandoned edits are dropped in favour of the applied filters.
        key={isOpen ? 'open' : 'closed'}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <ProjectsFilterModalForm onCancel={onClose} />
      </Formik>
    </Modal>
  );
};

export default ProjectsFilterModal;
